import { compare } from 'compare-versions'
import { pageVisibleOnce } from './page-visible-internal'
import {
  cancelPageLoadingRecovery,
  hasPageLoadingEnded,
  markPageLoadingReady,
  resetPageLoadingRecoveryForTests,
} from './page-loading-recovery-internal'
import type { AuthServiceMethods } from './services/auth'
import type { UserServiceBridge, UserServiceMethods } from './services/user'
import type { PermissionServiceMethods } from './services/permission'
import type { GeneralServiceMethods } from './services/general'
import type { IAPServiceMethods } from './services/iap'
import type { AdditionalServiceMethods } from './services/additional'
import type { L10nServiceMethods } from './services/l10n'
import type { DataServiceMethods } from './services/data'
import type { AssetsServiceMethods } from './services/assets'
import type { PaywallServiceMethods } from './services/paywall'
import type { ExperimentServiceMethods } from './services/experiment'
import type { NotificationServiceMethods } from './services/notification'

export interface NativeServices {
  auth: AuthServiceMethods
  user: UserServiceMethods
  permission: PermissionServiceMethods
  general: GeneralServiceMethods
  iap: IAPServiceMethods
  data: DataServiceMethods
  assets: AssetsServiceMethods
  paywall: PaywallServiceMethods
  /** Native ≥ 5.27.0; earlier versions report `not_registered`. */
  experiment: ExperimentServiceMethods
  /** Native ≥ 5.27.0; earlier versions report `unsupported`. */
  notification: NotificationServiceMethods
  // only for relax-paywall app
  additional: AdditionalServiceMethods
  l10n: {
    getLocalizedString: (payload: {
      key: string
      params?: { type: 'integer' | 'float' | 'string'; value: string }[]
    }) => Promise<string>
  }
}

interface InternalProxies {
  auth: AuthServiceMethods
  user: UserServiceBridge
  permission: PermissionServiceMethods
  general: GeneralServiceMethods
  iap: IAPServiceMethods
  data: DataServiceMethods
  assets: AssetsServiceMethods
  paywall: PaywallServiceMethods
  experiment: ExperimentServiceMethods
  notification: NotificationServiceMethods
  additional: AdditionalServiceMethods
  l10n: L10nServiceMethods
}

let proxies: InternalProxies | null = null
let appVersion: string = '0.0.0'

/** Native version that shipped ExperimentService and NotificationService. */
const EXPERIMENT_NOTIFICATION_MIN_VERSION = '5.27.0'

/** Native version whose UserService hands web the service level status. */
const SERVICE_LEVEL_MIN_VERSION = '5.27.0'

/**
 * Whether the host app is at least `min`. A version string the host sends
 * that is not a version at all (e.g. an empty `app_version` param) counts as
 * below every gate rather than throwing out of a `native.*` call.
 */
function appVersionAtLeast(min: string): boolean {
  try {
    return compare(appVersion, min, '>=')
  } catch {
    return false
  }
}

/** @internal — set by initializeNativeBridge. Throws if called twice. */
export function __setProxies(next: InternalProxies): void {
  if (proxies) throw new Error('native services already initialized')
  proxies = next
}

/** @internal — set by initializeNativeBridge. */
export function __setAppVersion(next: string): void {
  appVersion = next
}

/** @internal — test-only reset so each test starts from a clean module state. */
export function __resetForTests(): void {
  proxies = null
  appVersion = '0.0.0'
  pageVisibleOnce.__resetForTests()
  resetPageLoadingRecoveryForTests()
}

function requireProxy<K extends keyof InternalProxies>(
  key: K,
): InternalProxies[K] {
  if (!proxies) {
    throw new Error(
      'native services not initialized — call initializeNativeBridge() first',
    )
  }
  return proxies[key]
}

export const native: NativeServices = {
  auth: {
    auth: () => requireProxy('auth').auth(),
    bindLegacyUser: () => requireProxy('auth').bindLegacyUser(),
  },
  user: {
    getUserInfo: () => requireProxy('user').getUserInfo(),
    refreshToken: () => requireProxy('user').refreshToken(),
    getServiceLevel: async () => {
      if (!appVersionAtLeast(SERVICE_LEVEL_MIN_VERSION)) {
        return { success: false, error: 'unsupported' }
      }
      return requireProxy('user').getServiceLevel()
    },
    // Always calls native: older native still refreshes its cached service
    // level, which every fire-and-forget caller relies on — it just resolves
    // with no record. Not `async`, so calling before initialization still
    // throws synchronously, as it did when this returned `Promise<void>`.
    refreshServiceLevel: () =>
      requireProxy('user')
        .refreshServiceLevel()
        .then((result) =>
          result && appVersionAtLeast(SERVICE_LEVEL_MIN_VERSION)
            ? result
            : { success: false, error: 'unsupported' },
        ),
  },
  permission: {
    getHasNotificationPermission: () =>
      requireProxy('permission').getHasNotificationPermission(),
    requestNotificationPermission: () =>
      requireProxy('permission').requestNotificationPermission(),
  },
  general: {
    close: () => {
      cancelPageLoadingRecovery()
      return requireProxy('general').close()
    },
    logEvent: (p) => {
      console.log(
        '[native-services] logEvent',
        p.event_name,
        JSON.stringify(p.event_params ?? {}),
      )
      return requireProxy('general').logEvent(p)
    },
    openURL: (url) => requireProxy('general').openURL(url),
    pageReady: () => {
      // A page that has already produced its terminal outcome — close or
      // complete — must not then tell native it is ready. That would lift the
      // cover over a page that just closed: a second, competing outcome for one
      // presentation (spec Decisions 10 and 11). `close` is guarded the same way
      // at its call site; this is the symmetric web-side guard, and it is the
      // one that has to hold, because Forest web ships independently of the
      // native runtime and older native builds have no guard of their own.
      if (hasPageLoadingEnded()) return Promise.resolve()
      markPageLoadingReady()
      return requireProxy('general').pageReady()
    },
    complete: () => {
      cancelPageLoadingRecovery()
      return requireProxy('general').complete()
    },
    getPageContext: () => requireProxy('general').getPageContext(),
    logFatalError: (p) => requireProxy('general').logFatalError(p),
    logNonFatalError: (p) => requireProxy('general').logNonFatalError(p),
  },
  data: {
    fullSync: () => requireProxy('data').fullSync(),
  },
  assets: {
    getImageByName: (name) => requireProxy('assets').getImageByName(name),
  },
  paywall: {
    openPaywall: (...args) => requireProxy('paywall').openPaywall(...args),
  },
  iap: {
    getPrice: (p) => requireProxy('iap').getPrice(p),
    purchase: (p) => requireProxy('iap').purchase(p),
    closeTransaction: (p) => requireProxy('iap').closeTransaction(p),
    getUnclosedTransaction: (p) =>
      requireProxy('iap').getUnclosedTransaction(p),
    getAllUnclosedTransactions: () =>
      requireProxy('iap').getAllUnclosedTransactions(),
    getAllEntitlements: () => requireProxy('iap').getAllEntitlements(),
    getAvailableSubscriptionProducts: () =>
      requireProxy('iap').getAvailableSubscriptionProducts(),
    getStoreRegion: () => requireProxy('iap').getStoreRegion(),
    getReceiptSystemAccountId: () =>
      requireProxy('iap').getReceiptSystemAccountId(),
  },
  experiment: {
    getGroup: async (p) => {
      if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) {
        return { success: false, error: 'not_registered' }
      }
      return requireProxy('experiment').getGroup(p)
    },
  },
  notification: {
    schedule: async (p) => {
      if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) {
        return { success: false, error: 'unsupported' }
      }
      return requireProxy('notification').schedule(p)
    },
    getScheduled: async (p) => {
      if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) {
        return { success: false, error: 'unsupported' }
      }
      return requireProxy('notification').getScheduled(p)
    },
  },
  additional: {
    getInitialThemeId: () => requireProxy('additional').getInitialThemeId(),
  },
  l10n: {
    getLocalizedString: async (p) => {
      if (!appVersionAtLeast('5.17.0')) return p.key
      const result = await requireProxy('l10n').getLocalizedString(p)
      if (!result.success) return p.key
      return result.data ?? p.key
    },
  },
}

export { pageVisibleOnce }
